'use client'

import { useAuthState } from '@/app/providers/auth'
import Navigation from '@/components/navigation'
import PlatformAlertBanner from '@/components/PlatformAlertBanner'
import { SidebarShell } from '@/components/ui/SidebarShell'
import type { GmItemSearchQuery } from '@/generated/gql/graphql'
import { PortalType, type VendorHighlightCategoriesFilterInput } from '@/generated/graphql'
import { getDeliveryDetailsForMixpanel, useMixPanel } from '@/helpers/mixpanelConfig'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'
import useTenant from '@/hooks/useTenant'
import { Box, Container, Divider, Drawer, Grid, Stack } from '@mui/material'

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useGmFiltersActionsWithTransition } from '../supplier-menu/store/gmFiltersStore'
import { ActiveSupplierFilters } from './components/active-filters'
import { MobileSupplierFilters } from './components/mobile-supplier-filters'
import { SearchModeToggle } from './components/search-mode-toggle'
import { SidebarItemSearch } from './components/sidebar-item-search'
import { SupplierAvailabilitySelector } from './components/supplier-availability-selector'
import SupplierFilters from './components/supplier-filters/supplier-filters'
import SupplierSort from './components/supplier-filters/supplier-sort'
import SupplierType from './components/supplier-filters/supplier-type'
import { SupplierHighlightsCategory } from './components/supplier-highlight-category'
import SupplierList from './components/supplier-list'
import SupplierSearch from './components/supplier-search/supplier-search'
import {
  buildHighlightFilters,
  useVendorHighlightCategories,
} from './states/useVendorHighlightCategories'
import { useVendorListPageState } from './states/useVendorListPageState'
import useVendorSearchState from './states/useVendorSearchState'

const SIDEBAR_WIDTH = 280

const Suppliers = () => {
  const { t } = useTranslation()
  const [mobileOpen, setMobileOpen] = useState(false)
  const [navigationHeight, setNavigationHeight] = useState(0)
  const {
    called,
    filterOpen,
    toggleFilterOpen,
    search,
    filters,
    searching,
    searchComplete,
    stats,
    filterCount,
    defaultSearch,
    vendors,
    pageInfo,
  } = useVendorSearchState()
  const [searchMode, setSearchMode] = useState<'vendor' | 'item'>(() =>
    filters.itemSearch ? 'item' : 'vendor',
  )
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen)
  }
  const stickyOffset = navigationHeight > 0 ? navigationHeight : 80

  const { clearFilters } = useGmFiltersActionsWithTransition()

  useEffect(() => {
    // permalink filters cleared from store in Suppliers component in case user
    // uses browser/url navigation directly to return from specific vendor page
    // to prevent unwanted filter persistence between vendors
    clearFilters()
  }, [])

  const showHighlightsFlagOn = useFeatureFlag(SPLITS.SHOW_HIGHLIGHTS_GM)
  const mixPanelIndividualEvents = useFeatureFlag(SPLITS.MIX_PANEL_INDIVIDUAL_EVENTS)
  const mixPanel = useMixPanel()
  const tenant = useTenant()
  const auth = useAuthState()
  const showItemSearchVendorList = useFeatureFlag(SPLITS.GM_ITEM_SEARCH_VENDOR_LIST, {
    userId: auth.type === 'authenticated' ? auth.user?.id : '',
  })
  const { data: highlightsData, highlightFilters, fetchHighlights } = useVendorHighlightCategories()
  const filtersSignature = useMemo(() => JSON.stringify(filters), [filters])

  useEffect(() => {
    fetchHighlights(filters)
  }, [fetchHighlights, filtersSignature])

  const enhancedSearch = useCallback(
    (variables: Parameters<typeof search>[0]) => {
      search(variables)
    },
    [search],
  )
  const effectiveHighlightFilters = useMemo<VendorHighlightCategoriesFilterInput>(() => {
    return highlightFilters ?? buildHighlightFilters(tenant.id, filters)
  }, [highlightFilters, tenant.id, filters])
  const { userBasketSettings, minAllowedDeliveryDate, deliveryTimeSlots } = useVendorListPageState()

  const showHighlights =
    showHighlightsFlagOn &&
    Boolean(highlightsData) &&
    Boolean(filters.location) &&
    !filters.highlightsTags?.length

  const itemSearchClickTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearchModeChange = useCallback(
    (mode: 'vendor' | 'item') => {
      if (mode === searchMode) return
      if (searchMode === 'vendor' && mode === 'item') {
        itemSearchClickTimerRef.current = setTimeout(() => {
          try {
            const deliveryDetails = getDeliveryDetailsForMixpanel(userBasketSettings.value)
            mixPanel?.track('Item Search Click', {
              name: 'Item Search Click',
              ...deliveryDetails,
              customerAccount:
                auth.type === 'authenticated'
                  ? `${auth.user.account?.id}-${auth.user.account?.name}`
                  : 'guest',
              tenantName: tenant.name,
              tenantId: tenant.id,
              userId: auth.type === 'authenticated' ? auth.user?.id : null,
            })
          } catch (e) {
            console.error('Mixpanel tracking failed', e)
          }
        }, 10000) // 10 second timer to prevent accidental toggles from being tracked
      } else {
        if (itemSearchClickTimerRef.current) {
          clearTimeout(itemSearchClickTimerRef.current)
          itemSearchClickTimerRef.current = null
        }
      }
      setSearchMode(mode)
    },
    [searchMode, mixPanel, userBasketSettings.value, auth, tenant],
  )

  useEffect(() => {
    return () => {
      if (itemSearchClickTimerRef.current) clearTimeout(itemSearchClickTimerRef.current)
    }
  }, [])

  const handleItemSearchTriggered = useCallback(
    (searchTerm: string) => {
      if (showItemSearchVendorList) {
        enhancedSearch({ itemSearch: searchTerm })
      }
    },
    [showItemSearchVendorList, enhancedSearch],
  )

  const handleItemSearchComplete = useCallback(
    (searchTerm: string, results: GmItemSearchQuery['gmItemSearch'], hasError: boolean) => {
      try {
        const topResults = results?.slice(0, 3) // Only log top 3 results for now
        const deliveryDetails = getDeliveryDetailsForMixpanel(userBasketSettings.value)
        mixPanel?.track('Item Search', {
          name: 'Item Search',
          searchTerm,
          firstResult: topResults?.[0]?.supplierName ?? null,
          secondResult: topResults?.[1]?.supplierName ?? null,
          thirdResult: topResults?.[2]?.supplierName ?? null,
          hasError,
          ...deliveryDetails,
          customerAccount:
            auth.type === 'authenticated'
              ? `${auth.user.account?.id}-${auth.user.account?.name}`
              : 'guest',
          tenantName: tenant.name,
          tenantId: tenant.id,
          userid: auth.type === 'authenticated' ? auth.user?.id : null,
        })
      } catch (e) {
        console.error('Mixpanel tracking failed', e)
      }
    },
    [mixPanel, userBasketSettings.value, auth, tenant],
  )

  const handleItemSearchCleared = useCallback(() => {
    if (showItemSearchVendorList) {
      enhancedSearch({ itemSearch: '' })
    }
  }, [showItemSearchVendorList, enhancedSearch])

  const hasTrackedMixPanelRef = useRef(false)

  useEffect(() => {
    if (
      !mixPanelIndividualEvents ||
      hasTrackedMixPanelRef.current ||
      auth.type === 'loading' ||
      !mixPanel?.checkCanUse() ||
      !userBasketSettings.value.location ||
      !userBasketSettings.value.date ||
      !userBasketSettings.value.time
    ) {
      return
    }

    try {
      const deliveryDetails = getDeliveryDetailsForMixpanel(userBasketSettings.value)
      mixPanel.track('Supplier List Viewed', {
        name: 'Supplier List Viewed',
        ...deliveryDetails,
        customerAccount:
          auth.type === 'authenticated'
            ? `${auth.user.account?.id}-${auth.user.account?.name}`
            : 'guest',
        tenantName: tenant.name,
        tenantId: tenant.id,
        userId: auth.type === 'authenticated' ? auth.user?.id : null,
      })
      hasTrackedMixPanelRef.current = true
    } catch (e) {
      console.error('Mixpanel tracking failed', e)
    }
  }, [mixPanelIndividualEvents, mixPanel, tenant, auth.type, userBasketSettings.value])

  const prevSearchCompleteRef = useRef(false)

  useEffect(() => {
    const wasComplete = prevSearchCompleteRef.current
    prevSearchCompleteRef.current = searchComplete

    if (showItemSearchVendorList && !wasComplete && searchComplete && filters.itemSearch) {
      const mappedResults = (vendors ?? []).map((v) => ({
        supplierName: v.companyName ?? null,
      })) as GmItemSearchQuery['gmItemSearch']

      handleItemSearchComplete(filters.itemSearch, mappedResults, false)
    }
  }, [
    searchComplete,
    showItemSearchVendorList,
    filters.itemSearch,
    vendors,
    handleItemSearchComplete,
  ])

  return (
    <Box>
      <PlatformAlertBanner portalType={PortalType.Catering} />
      <Navigation onHeightChange={setNavigationHeight} />

      <Stack direction="row">
        {/* Sidebar */}
        <Box sx={{ display: { xs: 'none', lg: 'block' } }}>
          <SidebarShell
            ariaLabel="Supplier filters"
            width={SIDEBAR_WIDTH}
            stickyOffset={stickyOffset}>
            {/* Sticky SupplierSearch */}
            <Box
              sx={{
                position: 'sticky',
                top: 0,
                zIndex: 10,
                backgroundColor: 'background.paper',
                p: 2,
              }}>
              <>
                <SearchModeToggle searchMode={searchMode} onModeChange={handleSearchModeChange} />
                <Box>
                  {searchMode === 'item' ? (
                    <SidebarItemSearch
                      tenantId={tenant.id}
                      location={userBasketSettings.value.location}
                      filters={filters}
                      onSearchTriggered={handleItemSearchTriggered}
                      onSearchComplete={handleItemSearchComplete}
                      onSearchCleared={handleItemSearchCleared}
                      disableSidebarQuery={showItemSearchVendorList}
                      searching={searching}
                    />
                  ) : (
                    <SupplierSearch
                      called={called}
                      filterCount={filterCount}
                      filters={filters}
                      search={enhancedSearch}
                      toggleFilterOpen={toggleFilterOpen}
                      userBasketSettings={userBasketSettings.value}
                      placeholder={t('marketplace:searchForAVendor', 'Search for a vendor...')}
                      helperText={t('marketplace:findVendorByName', 'Find a vendor by name')}
                    />
                  )}
                </Box>
              </>
              <Divider flexItem sx={{ mt: 2 }} />
            </Box>

            {/* Scrollable Filters */}
            <Box
              sx={{
                width: '100%',
                flex: 1,
                minHeight: 0,
                scrollbarWidth: 'thin',
                p: 2,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                overflow: 'auto',
                borderTop: 'none',
                borderRadius: 0,
              }}>
              <ActiveSupplierFilters filters={filters} search={enhancedSearch} />
              <SupplierType filters={filters} search={enhancedSearch} />
              {called ? (
                <SupplierFilters
                  searching={searching}
                  stats={stats}
                  filters={filters}
                  filterOpen={filterOpen}
                  toggleFilterOpen={toggleFilterOpen}
                  search={enhancedSearch}
                />
              ) : null}
            </Box>
          </SidebarShell>
        </Box>

        {/* Mobile Sidebar Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          disableScrollLock
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', lg: 'none' },
            '& .MuiDrawer-paper': {
              // width: '80vw',
              maxWidth: SIDEBAR_WIDTH,
            },
          }}>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <SearchModeToggle searchMode={searchMode} onModeChange={handleSearchModeChange} />
            {searchMode === 'item' ? (
              <SidebarItemSearch
                tenantId={tenant.id}
                location={userBasketSettings.value.location}
                filters={filters}
                onSearchTriggered={handleItemSearchTriggered}
                onSearchComplete={handleItemSearchComplete}
                onSearchCleared={handleItemSearchCleared}
                disableSidebarQuery={showItemSearchVendorList}
                searching={searching}
              />
            ) : (
              <SupplierSearch
                called={called}
                filterCount={filterCount}
                filters={filters}
                search={enhancedSearch}
                toggleFilterOpen={toggleFilterOpen}
                userBasketSettings={userBasketSettings.value}
                placeholder={t('marketplace:searchForAVendor', 'Search for a vendor...')}
                helperText={t('marketplace:findVendorByName', 'Find a vendor by name')}
              />
            )}
            <ActiveSupplierFilters filters={filters} search={enhancedSearch} />
            <SupplierType filters={filters} search={enhancedSearch} />
            {called ? (
              <SupplierFilters
                searching={searching}
                stats={stats}
                filters={filters}
                filterOpen={filterOpen}
                toggleFilterOpen={toggleFilterOpen}
                search={enhancedSearch}
              />
            ) : null}
          </Box>
        </Drawer>

        {/* Main Content */}
        <Stack component="main" flexGrow={1} gap={2} minWidth={0}>
          <Box
            sx={{
              width: '100%',
              bgcolor: 'background.paper',
              minWidth: 0,
            }}>
            <Box
              sx={{
                p: { xs: 1, md: 2 },
                width: '100%',
                maxWidth: {
                  xl: 'xl',
                },
                minWidth: 0,
              }}>
              <Grid container spacing={2} sx={{ mb: { xs: 2, md: 4 } }}>
                <Grid size={{ xs: 9, md: 9 }}>
                  <SupplierAvailabilitySelector
                    called={called}
                    userBasketSettings={userBasketSettings.value}
                    minAllowedDate={minAllowedDeliveryDate}
                    timeSlots={deliveryTimeSlots}
                    onUserBasketSettingsChange={userBasketSettings.set}
                    vendorType={filters.supplierType || t('marketplace:all')}
                  />
                </Grid>

                <Grid size={{ xs: 3, md: 3 }} display="flex" justifyContent="flex-end">
                  <SupplierSort filters={filters} search={enhancedSearch} />
                </Grid>
              </Grid>

              <MobileSupplierFilters
                onFilterClick={handleDrawerToggle}
                filters={filters}
                search={enhancedSearch}
              />

              <Box>
                {showHighlights && filters.location ? (
                  <SupplierHighlightsCategory
                    location={filters.location}
                    search={enhancedSearch}
                    highlightFilters={effectiveHighlightFilters}
                    highlightCategoryData={
                      highlightsData?.vendorHighlightsForLocation.highlightCategories ?? []
                    }
                  />
                ) : null}
              </Box>
            </Box>
          </Box>
          <Box
            sx={{
              p: { xs: 2, md: 2 },
              width: '100%',
              maxWidth: {
                xl: 'xl',
              },
              minWidth: 0,
            }}>
            <Container disableGutters sx={{ mb: 2, maxWidth: { xs: 'lg', xl: 'xl' } }}>
              <Stack
                sx={{
                  backgroundColor: 'background.default',
                  gap: 2,
                }}>
                <SupplierList
                  filters={filters}
                  called={called}
                  defaultSearch={defaultSearch}
                  vendors={vendors}
                  pageInfo={pageInfo}
                  search={enhancedSearch}
                  searching={searching}
                />
              </Stack>
            </Container>
          </Box>
        </Stack>
      </Stack>
    </Box>
  )
}

export default Suppliers
export { Suppliers }
